import PropTypes from "prop-types";
import * as React from "react";
import fetcher, { HTTPMethods } from "../../../hooks/fetcher";
import { Button, CameraIcon, Dialog, Slider, Spinner } from "../../../themeV2";
import getCroppedImage from "../../../util/croppedImage";
import css from "./AvatarEditDialog.module.css";

const Cropper = React.lazy(() => import("react-easy-crop"));

const AvatarEditDialog = React.memo(function AvatarEditDialog({
  open,
  onClose,
  influencerId,
  onSaved,
}) {
  const inputRef = React.useRef(null);
  const [imageSrc, setImageSrc] = React.useState(null);
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setImageSrc(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setIsSaving(false);
      setError(null);
    }
  }, [open]);

  const handleOpenChange = React.useCallback(
    (nextOpen) => {
      if (!nextOpen && !isSaving) {
        onClose();
      }
    },
    [isSaving, onClose],
  );

  const handleFileSelect = React.useCallback((evt) => {
    const file = evt.target.files?.[0];
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const handleChangeImage = React.useCallback(() => {
    setImageSrc(null);
    setZoom(1);
    setError(null);
    requestAnimationFrame(() => {
      inputRef.current?.click();
    });
  }, []);

  const handleCropComplete = React.useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleSave = React.useCallback(async () => {
    if (isSaving || !imageSrc || !croppedAreaPixels) return;

    setError(null);
    setIsSaving(true);

    try {
      const base64 = await getCroppedImage(imageSrc, croppedAreaPixels, 0);

      await fetcher(`/api/influencer/${influencerId}`, {
        method: HTTPMethods.PUT,
        body: JSON.stringify({ avatar: base64 }),
      });

      onSaved(base64);
      onClose();
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setIsSaving(false);
    }
  }, [isSaving, imageSrc, croppedAreaPixels, influencerId, onSaved, onClose]);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Popup size="md">
        <Dialog.Title>Edit avatar</Dialog.Title>
        <Dialog.Close />

        <div className={css.content}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileSelect}
            aria-label="Select avatar image file"
          />

          {imageSrc ? (
            <>
              <div className={css.toolbar}>
                <Button
                  variant="tonal"
                  size="sm"
                  onClick={handleChangeImage}
                  leading={<CameraIcon />}
                >
                  Change Image
                </Button>

                <div className={css.zoomWrapper}>
                  <Slider
                    label="Zoom"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onValueChange={setZoom}
                  />
                </div>
              </div>

              <div className={css.cropArea}>
                <React.Suspense
                  fallback={
                    <Spinner
                      size="small"
                      style={{ position: "absolute", top: "50%", left: "50%" }}
                    />
                  }
                >
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    cropShape="round"
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onCropComplete={handleCropComplete}
                    onZoomChange={setZoom}
                  />
                </React.Suspense>
              </div>
            </>
          ) : (
            <div className={css.pickerArea}>
              <Button variant="tonal" as="label" leading={<CameraIcon />}>
                Select Avatar Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileSelect}
                  aria-label="Select avatar image file"
                />
              </Button>
            </div>
          )}

          {Boolean(error) && <p className={css.error}>{error}</p>}
        </div>

        <div className={css.actions}>
          <Button variant="text" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSave}
            disabled={isSaving || !imageSrc}
            loading={isSaving}
          >
            Save Avatar
          </Button>
        </div>
      </Dialog.Popup>
    </Dialog.Root>
  );
});

AvatarEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  influencerId: PropTypes.number.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default AvatarEditDialog;
